import React, { useState } from "react";
import OverallDonut from "../OverallDonut";
import {
  BarChart,
  Bar,
  Cell,
  ComposedChart,
  Line,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import exampleApps10 from "../../utils/aisprawl/example-apps-10.json";
import "../../styles/aisprawl/aisprawl.css";
import AisprawlEndpointsChart from "./AisprawlEndpointsChart";

const BAR_COLORS_TYPE = ["#fecaca", "#fca5a5", "#f87171", "#ef4444", "#b91c1c"];
const BAR_COLORS_VENDOR = ["#fee2e2", "#fecaca", "#fca5a5", "#f87171", "#ef4444"];

function LineChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0]?.value;

  return (
    <div
      style={{
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: 10,
        padding: "8px 10px",
        color: "var(--body-text)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Total AI Endpoints</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#3b82f6" }}>
          {typeof value === "number" ? value : "--"}
        </span>
      </div>
    </div>
  );
}

function BarChartTooltip({ active, payload, label, accent = "#ef4444" }) {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0]?.value;

  return (
    <div
      style={{
        background: "var(--card-bg)",
        border: "1px solid var(--card-border)",
        borderRadius: 10,
        padding: "8px 10px",
        color: "var(--body-text)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      }}
    >
      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Count</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: accent }}>
          {typeof value === "number" ? value : "--"}
        </span>
      </div>
    </div>
  );
}

const STAT_LABELS = [
  "Total Endpoints Scanned",
  "Discovered AI Endpoints",
  "Authorized AI Apps",
  "Unauthorized AI Apps",
  "Blocked Apps",
  "Monitored Apps",
];

const BUCKET_LABELS = {
  ai_coding_assistant: "AI Coding Assistant",
  enterprise_ai_copilot: "Enterprise AI Copilot",
  generative_ai_chatbot: "Generative AI Chatbot",
};

function getBucketLabel(bucket) {
  if (!bucket) return "";
  return BUCKET_LABELS[bucket] || bucket.replace(/_/g, " ");
}

function getRandomStats() {
  const total = 50 + Math.floor(Math.random() * 150);
  const discovered = Math.floor(total * (0.2 + Math.random() * 0.4));
  const authorized = Math.floor(discovered * (0.4 + Math.random() * 0.4));
  const unauthorized = discovered - authorized;
  const blocked = Math.floor(unauthorized * (0.3 + Math.random() * 0.5));
  const monitored = discovered - blocked;
  return {
    [STAT_LABELS[0]]: total,
    [STAT_LABELS[1]]: discovered,
    [STAT_LABELS[2]]: authorized,
    [STAT_LABELS[3]]: unauthorized,
    [STAT_LABELS[4]]: blocked,
    [STAT_LABELS[5]]: Math.max(0, monitored),
  };
}

export default function AisprawlDashboard() {
  const [stats] = useState(getRandomStats);
  const scannedTotal = 100;
  const discoveredTotal = 36;
  const discoveredPercent =
    scannedTotal > 0 ? (discoveredTotal / scannedTotal) * 100 : 0;

  const [endpointsView, setEndpointsView] = useState("type");

  const endpointsByType = React.useMemo(() => {
    const bucketToDeviceInfo = new Map();

    (exampleApps10 ?? []).forEach((endpoint) => {
      const deviceId = endpoint.device_id;
      const deviceName = endpoint.device_name;
      const edrSource = endpoint.edr_source;

      (endpoint.apps ?? []).forEach((app) => {
        const bucket = app.bucket || "Unknown";
        if (!bucketToDeviceInfo.has(bucket)) {
          bucketToDeviceInfo.set(bucket, new Map());
        }

        const deviceMap = bucketToDeviceInfo.get(bucket);
        const existing = deviceMap.get(deviceName);
        const confidence = app.confidence ?? 0;

        if (!existing || confidence > (existing.confidence ?? 0)) {
          deviceMap.set(deviceName, {
            bucket,
            deviceId,
            deviceName,
            edrSource,
            appName: app.app,
            publisher: app.publisher,
            confidence,
            action: app.action,
          });
        }
      });
    });

    return Array.from(bucketToDeviceInfo.entries())
      .map(([bucket, deviceMap]) => ({
        bucket,
        endpoints: Array.from(deviceMap.values()).sort((a, b) =>
          a.deviceName.localeCompare(b.deviceName)
        ),
      }))
      .sort((a, b) => a.bucket.localeCompare(b.bucket));
  }, []);

  const endpointsByApp = React.useMemo(() => {
    const appToDeviceInfo = new Map();

    (exampleApps10 ?? []).forEach((endpoint) => {
      const deviceId = endpoint.device_id;
      const deviceName = endpoint.device_name;
      const edrSource = endpoint.edr_source;

      (endpoint.apps ?? []).forEach((app) => {
        const appName = app.app || "Unknown App";
        if (!appToDeviceInfo.has(appName)) {
          appToDeviceInfo.set(appName, new Map());
        }

        const deviceMap = appToDeviceInfo.get(appName);
        const existing = deviceMap.get(deviceName);
        const confidence = app.confidence ?? 0;

        if (!existing || confidence > (existing.confidence ?? 0)) {
          deviceMap.set(deviceName, {
            bucket: app.bucket || "Unknown",
            deviceId,
            deviceName,
            edrSource,
            appName,
            publisher: app.publisher,
            confidence,
            action: app.action,
          });
        }
      });
    });

    return Array.from(appToDeviceInfo.entries())
      .map(([appName, deviceMap]) => ({
        appName,
        endpoints: Array.from(deviceMap.values()).sort((a, b) =>
          a.deviceName.localeCompare(b.deviceName)
        ),
      }))
      .sort((a, b) => a.appName.localeCompare(b.appName));
  }, []);

  const endpointsByVendor = React.useMemo(() => {
    const vendorToDeviceInfo = new Map();

    (exampleApps10 ?? []).forEach((endpoint) => {
      const deviceId = endpoint.device_id;
      const deviceName = endpoint.device_name;
      const edrSource = endpoint.edr_source;

      (endpoint.apps ?? []).forEach((app) => {
        const publisher = app.publisher || "Unknown Vendor";
        if (!vendorToDeviceInfo.has(publisher)) {
          vendorToDeviceInfo.set(publisher, new Map());
        }

        const deviceMap = vendorToDeviceInfo.get(publisher);
        const existing = deviceMap.get(deviceName);
        const confidence = app.confidence ?? 0;

        if (!existing || confidence > (existing.confidence ?? 0)) {
          deviceMap.set(deviceName, {
            bucket: app.bucket || "Unknown",
            deviceId,
            deviceName,
            edrSource,
            appName: app.app,
            publisher,
            confidence,
            action: app.action,
          });
        }
      });
    });

    return Array.from(vendorToDeviceInfo.entries())
      .map(([publisher, deviceMap]) => ({
        publisher,
        endpoints: Array.from(deviceMap.values()).sort((a, b) =>
          a.deviceName.localeCompare(b.deviceName)
        ),
      }))
      .sort((a, b) => a.publisher.localeCompare(b.publisher));
  }, []);

  const endpointsByName = React.useMemo(() => {
    const nameToAppInfo = new Map();

    (exampleApps10 ?? []).forEach((endpoint) => {
      const deviceId = endpoint.device_id;
      const deviceName = endpoint.device_name;
      const edrSource = endpoint.edr_source;

      (endpoint.apps ?? []).forEach((app) => {
        if (!nameToAppInfo.has(deviceName)) {
          nameToAppInfo.set(deviceName, new Map());
        }

        const appMap = nameToAppInfo.get(deviceName);
        const appName = app.app || "Unknown App";
        const existing = appMap.get(appName);
        const confidence = app.confidence ?? 0;

        if (!existing || confidence > (existing.confidence ?? 0)) {
          appMap.set(appName, {
            bucket: app.bucket || "Unknown",
            deviceId,
            deviceName,
            edrSource,
            appName,
            publisher: app.publisher,
            confidence,
            action: app.action,
          });
        }
      });
    });

    return Array.from(nameToAppInfo.entries())
      .map(([deviceName, appMap]) => ({
        deviceName,
        endpoints: Array.from(appMap.values()).sort((a, b) =>
          a.appName.localeCompare(b.appName)
        ),
      }))
      .sort((a, b) => a.deviceName.localeCompare(b.deviceName));
  }, []);

  return (
    <>
      <div className="grid grid-aisprawl">
        <div className="stats-card">
          <p>AI Endpoints</p>
          <OverallDonut value={discoveredPercent} />
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              justifyContent: "space-between",
              fontSize: "12px",
              alignItems: "flex-start",
            }}
          >
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "18px",
                  lineHeight: 1.1,
                }}
              >
                {scannedTotal}
              </div>
              <div style={{ opacity: 0.8, marginTop: "2px" }}>Scanned</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "18px",
                  lineHeight: 1.1,
                }}
              >
                {discoveredTotal}
              </div>
              <div style={{ opacity: 0.8, marginTop: "2px" }}>Discovered</div>
            </div>
          </div>
        </div>
        <div className="stats-card" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <p>Top 5 AI Endpoints by Type</p>
          <div style={{ width: "100%", height: 210, display: "flex", justifyContent: "center" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: "Chatbot", value: 152, color: BAR_COLORS_TYPE[0] },
                  { name: "Assistant", value: 41, color: BAR_COLORS_TYPE[1] },
                  { name: "Copilot", value: 28, color: BAR_COLORS_TYPE[2] },
                  { name: "Misc", value: 19, color: BAR_COLORS_TYPE[3] },
                  { name: "Other", value: 11, color: BAR_COLORS_TYPE[4] },
                ]}
                layout="vertical"
                // Center X-axis: left gap (8 + yAxis width 56) == right gap (64)
                // This is a 20% reduction from the original 80px gaps
                margin={{ top: 8, right: 64, bottom: 8, left: 8 }}
              >
                <XAxis
                  type="number"
                  tickLine={false}
                  domain={[0, 160]}
                  ticks={[0, 40, 80, 120, 160]}
                  axisLine={{ stroke: "var(--card-border)" }}
                  tick={{ fontSize: 10, fill: "var(--body-text)" }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={56}
                  tick={{ fontSize: 11, fill: "var(--body-text)" }}
                />
                <Tooltip
                  content={(props) => (
                    <BarChartTooltip
                      {...props}
                      accent={props.payload?.[0]?.payload?.color ?? "#ef4444"}
                    />
                  )}
                  cursor={{ fill: "transparent" }}
                />
                <Bar
                  dataKey="value"
                  radius={[0, 4, 4, 0]}
                  barSize={26}
                  activeBar={{ radius: [0, 4, 4, 0] }}
                >
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Cell key={i} fill={BAR_COLORS_TYPE[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="stats-card" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <p>Top 5 AI Endpoints by Vendor</p>
          <div style={{ width: "100%", height: 210, display: "flex", justifyContent: "center" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={[
                  { name: "OpenAI", value: 47, color: BAR_COLORS_VENDOR[0] },
                  { name: "Azure", value: 35, color: BAR_COLORS_VENDOR[1] },
                  { name: "GCP", value: 29, color: BAR_COLORS_VENDOR[2] },
                  { name: "AWS", value: 22, color: BAR_COLORS_VENDOR[3] },
                  { name: "Anthropic", value: 8, color: BAR_COLORS_VENDOR[4] },
                ]}
                layout="vertical"
                // Center X-axis: left gap (8 + yAxis width 56) == right gap (64)
                margin={{ top: 8, right: 64, bottom: 8, left: 8 }}
              >
                <XAxis
                  type="number"
                  tickLine={false}
                  domain={[0, 50]}
                  ticks={[0, 10, 20, 30, 40, 50]}
                  axisLine={{ stroke: "var(--card-border)" }}
                  tick={{ fontSize: 10, fill: "var(--body-text)" }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={56}
                  tick={{ fontSize: 11, fill: "var(--body-text)" }}
                />
                <Tooltip
                  content={(props) => (
                    <BarChartTooltip
                      {...props}
                      accent={props.payload?.[0]?.payload?.color ?? "#ef4444"}
                    />
                  )}
                  cursor={{ fill: "transparent" }}
                />
                <Bar
                  dataKey="value"
                  radius={[0, 4, 4, 0]}
                  barSize={26}
                  activeBar={{ radius: [0, 4, 4, 0] }}
                >
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Cell key={i} fill={BAR_COLORS_VENDOR[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* AI Endpoints Detected – commented out, bring back when needed */}
        <div className="stats-card aisprawl-span-2">
          <p>AI Endpoints Detected</p>
          <div style={{ width: "100%", height: 220, marginTop: "8px" }}>
            <ResponsiveContainer>
              <ComposedChart
                data={[
                  { month: "Feb '26", total: 18 },
                  { month: "Jan '26", total: 22 },
                  { month: "Dec '25", total: 25 },
                  { month: "Nov '25", total: 28 },
                  { month: "Oct '25", total: 10 },
                  { month: "Sep '25", total: 36 },
                ]}
                // Center X-axis in card: left gap (20 + yAxis width 40) == right gap (60)
                margin={{ top: 10, right: 60, bottom: 10, left: 20 }}
              >
                <defs>
                  <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  stroke="var(--card-border)"
                  strokeDasharray="3 3"
                  opacity={0.25}
                />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: "var(--body-text)" }}
                  tickLine={false}
                  axisLine={{ stroke: "var(--card-border)" }}
                />
                <YAxis
                  yAxisId="total"
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: "var(--body-text)" }}
                  tickMargin={8}
                  tickLine={false}
                  axisLine={{ stroke: "var(--card-border)" }}
                  width={40}
                  domain={[0, "dataMax + 5"]}
                  label={{
                    value: "Total AI Endpoints",
                    angle: -90,
                    position: "insideLeft",
                    style: {
                      fill: "var(--body-text)",
                      fontSize: 11,
                      textAnchor: "middle",
                    },
                  }}
                />
                <Tooltip
                  content={<LineChartTooltip />}
                  cursor={{ stroke: "var(--card-border)", strokeWidth: 1 }}
                />
                <Area
                  type="linear"
                  dataKey="total"
                  yAxisId="total"
                  fill="url(#lineGradient)"
                  fillOpacity={1}
                  baseValue={0}
                  stroke="none"
                />
                <Line
                  type="linear"
                  dataKey="total"
                  yAxisId="total"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: "#3b82f6", r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Statistics – commented out, bring back when needed */}
        <div className="stats-card">
          <p>Statistics</p>
          <div
            style={{
              fontSize: "14px",
              textAlign: "left",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {STAT_LABELS.map((label) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "4px 0",
                  borderBottom: "1px solid var(--card-border)",
                }}
              >
                <span>{label}</span>
                <span
                  style={{
                    padding: "2px 10px",
                    borderRadius: "999px",
                    border: "1px solid var(--card-border)",
                    background: "var(--card-bg)",
                    fontWeight: 600,
                    minWidth: "2.5rem",
                    textAlign: "center",
                  }}
                >
                  {stats[label] ?? "--"}
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="stats-card aisprawl-span-3 aisprawl-endpoints-toggle-card">
          <div className="aisprawl-endpoints-toggle-row">
            <select
              className="aisprawl-endpoints-toggle-select"
              value={endpointsView}
              onChange={(e) => setEndpointsView(e.target.value)}
            >
              <option value="type">Endpoints by Type</option>
              <option value="app">Endpoints by App</option>
              <option value="vendor">Endpoints by Vendor</option>
              <option value="name">Endpoints by Name</option>
            </select>
          </div>
          {endpointsView === "type" && (
            <AisprawlEndpointsChart
              key="type"
              groups={endpointsByType}
              getGroupKey={(group) => group.bucket}
              getGroupLabel={(group) => getBucketLabel(group.bucket)}
            />
          )}
          {endpointsView === "app" && (
            <AisprawlEndpointsChart
              key="app"
              groups={endpointsByApp}
              getGroupKey={(group) => group.appName}
              getGroupLabel={(group) => group.appName}
            />
          )}
          {endpointsView === "vendor" && (
            <AisprawlEndpointsChart
              key="vendor"
              groups={endpointsByVendor}
              getGroupKey={(group) => group.publisher}
              getGroupLabel={(group) => group.publisher}
            />
          )}
          {endpointsView === "name" && (
            <AisprawlEndpointsChart
              key="name"
              groups={endpointsByName}
              getGroupKey={(group) => group.deviceName}
              getGroupLabel={(group) => group.deviceName}
              getEndpointLabel={(endpoint) => endpoint.appName}
            />
          )}
        </div>
      </div>
    </>
  );
}
